import React from "react";
import { Wifi, BatteryFull, Signal } from "lucide-react";

/**
 * MobileFrame — Premium iPhone-style preview with dynamic island.
 */
export default function MobileFrame({
  appName = "My App",
  shortName = "MyApp",
  iconUrl = "",
  themeColor = "#0A0A0A",
  backgroundColor = "#FAFAF7",
  url = "",
}) {
  const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const initial = (shortName || appName || "A").trim().charAt(0).toUpperCase();
  const host = (() => {
    try {
      return new URL(url.startsWith("http") ? url : `https://${url || "example.com"}`).hostname.replace("www.", "");
    } catch { return "example.com"; }
  })();

  return (
    <div className="iphone" data-testid="mobile-frame">
      <div className="iphone-screen" style={{ background: backgroundColor }}>
        <div className="iphone-island" />

        {/* Status bar */}
        <div className="absolute top-2.5 inset-x-0 px-7 flex items-center justify-between status-bar text-zinc-900">
          <span className="font-semibold tracking-tight">{time}</span>
          <span className="flex items-center gap-1.5">
            <Signal className="w-3 h-3" strokeWidth={2.5} />
            <Wifi className="w-3 h-3" strokeWidth={2.5} />
            <BatteryFull className="w-4 h-4" strokeWidth={2.5} />
          </span>
        </div>

        {/* Wallpaper subtle gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${themeColor}22, transparent 50%)`,
          }}
        />

        {/* Home screen */}
        <div className="relative z-10 pt-12 pb-16 px-6 flex flex-col h-full">
          <div className="text-[10px] font-medium text-zinc-500 mb-3 px-1 truncate">{host}</div>

          <div className="grid grid-cols-4 gap-3.5 content-start">
            {/* The user's app tile */}
            <div className="flex flex-col items-center" data-testid="mobile-frame-app-tile">
              <div
                className="w-12 h-12 rounded-[14px] overflow-hidden grid place-items-center"
                style={{
                  background: themeColor,
                  boxShadow: "0 6px 16px -4px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.06)",
                }}
              >
                {iconUrl ? (
                  <img
                    src={iconUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span className="font-display font-bold text-lg text-white">{initial}</span>
                )}
              </div>
              <span className="mt-1 text-[9.5px] font-medium max-w-full truncate text-center text-zinc-800">
                {shortName || appName}
              </span>
            </div>

            {/* Decorative tiles */}
            {Array.from({ length: 11 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center opacity-50">
                <div
                  className="w-12 h-12 rounded-[14px] bg-white border border-zinc-200/80"
                  style={{ boxShadow: "0 2px 6px -2px rgba(0,0,0,0.08)" }}
                />
                <span className="mt-1 h-2 w-7 bg-zinc-300/60 rounded" />
              </div>
            ))}
          </div>

          <div className="mt-auto pb-1">
            <div className="h-1 w-1/3 bg-zinc-700/80 mx-auto rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
